import { Layout } from 'components/Layout/Layout';
import { Loader } from 'components/Loader/Loader';
import { SlipEvents } from 'components/SlipEvents/SlipEvents';
import { UgPager } from 'components/UgPager/UgPager';
import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import { SlipDetails } from 'components/mobile/SlipDetails/SlipDetails';
import { AccountStatementMobile } from 'components/mobile/AccountStatement/AccountStatement';
import { UserContext } from 'context/userContext';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useListScrollRestore } from 'hooks/useListScrollRestore';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import {
  AccountStatementRow,
  getAccountStatement,
  getTicketForPlayer,
} from 'services/api/accountStatementService';
import { Slip } from 'types/Slip';
import styles from '../styles/pages/AccountStatement.module.css';
import filterStyles from 'components/ClosedBetsFilter/ClosedBetsFilter.module.css';
import { useTranslation } from 'react-i18next';
import { STATEMENT_TX_TYPES, txTypeLabel } from 'helpers/txTypes';

const PAGE_SIZE = 15;

function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDateInput(v: string, endOfDay = false): Date | undefined {
  if (!v) return undefined;
  const [y, m, d] = v.split('-').map((x) => Number.parseInt(x, 10));
  if (!y || !m || !d) return undefined;
  const dt = new Date(
    y,
    m - 1,
    d,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0
  );
  return Number.isNaN(dt.getTime()) ? undefined : dt;
}

export const AccountStatement = () => {
  const { loggedUser } = useContext(UserContext);
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('0');
  const [transactionType, setTransactionType] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>(
    toDateInputValue(new Date())
  );
  const [endDate, setEndDate] = useState<string>(toDateInputValue(new Date()));
  const [rows, setRows] = useState<AccountStatementRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState<Slip | null>(null);
  const listScroll = useListScrollRestore(selectedSlip != null, isMobile);
  const [loadingSlip, setLoadingSlip] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: (() => {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return now;
    })(),
    toDate: (() => {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      return now;
    })(),
    transactionType: 'all',
  });

  useEffect(() => {
    if (!loggedUser) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await getAccountStatement(
        loggedUser,
        page,
        PAGE_SIZE,
        appliedFilters
      );
      if (cancelled) return;
      setRows(res?.rows ?? []);
      setTotalPages(res?.total_pages ?? 1);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loggedUser, page, appliedFilters]);

  // Every change of period, dates or type applies at once - no "Show"
  // button. The first render is skipped: the list already loads today.
  const filtersReady = useRef(false);
  useEffect(() => {
    if (!filtersReady.current) {
      filtersReady.current = true;
      return;
    }
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = new Date();
    to.setHours(23, 59, 59, 999);
    if (selectedPeriod === 'custom_date') {
      const customFrom = parseDateInput(startDate, false);
      const customTo = parseDateInput(endDate, true);
      // A date still being typed waits until it is whole.
      if (!customFrom || !customTo) return;
      setAppliedFilters({
        fromDate: customFrom,
        toDate: customTo,
        transactionType,
      });
    } else {
      if (selectedPeriod !== '0') {
        from.setDate(from.getDate() - Number.parseInt(selectedPeriod, 10));
      }
      setAppliedFilters({ fromDate: from, toDate: to, transactionType });
    }
    setPage(1);
  }, [selectedPeriod, startDate, endDate, transactionType]);

  if (!loggedUser) {
    navigate('/login');
    return null;
  }

  const openSlip = async (ticketId: number | null) => {
    if (!ticketId || !loggedUser) return;
    listScroll.remember();
    setLoadingSlip(true);
    const slip = await getTicketForPlayer(loggedUser, ticketId);
    setSelectedSlip(slip);
    setLoadingSlip(false);
  };

  const content = (
    <div className={styles.wrapper}>
      <h2 className={styles.title}>
        {t('account_statement_label').toUpperCase()}
      </h2>

      <div className={filterStyles.closedBetsFormWrapper}>
        <div className={filterStyles.formInputValue}>
          <span>{t('select_period_label')}</span>
          <select
            value={selectedPeriod}
            onChange={(event) => setSelectedPeriod(event.target.value)}
          >
            <option value="0">{t('today_label')}</option>
            <option value="3">{t('last_3_days_label')}</option>
            <option value="7">{t('last_7_days_label')}</option>
            <option value="14">{t('last_14_days_label')}</option>
            <option value="30">{t('last_30_days_label')}</option>
            <option value="60">{t('last_60_days_label')}</option>
            <option value="custom_date">{t('another_period_label')}</option>
          </select>
        </div>
        {selectedPeriod === 'custom_date' && (
          <>
            <div className={filterStyles.formInputValue}>
              <span>{t('start_date_label')}</span>
              <input
                className={styles.filterDate}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className={filterStyles.formInputValue}>
              <span>{t('end_date_label')}</span>
              <input
                className={styles.filterDate}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </>
        )}
        <div className={filterStyles.formInputValue}>
          <span>{t('tx_type_select_label')}</span>
          <select
            value={transactionType}
            onChange={(event) => setTransactionType(event.target.value)}
          >
            <option value="all">{t('tx_all_types')}</option>
            {STATEMENT_TX_TYPES.map((type) => (
              <option key={type} value={type}>
                {txTypeLabel(t, type)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <div className={styles.headerRow}>
          <div>ID</div>
          <div>{t('tx_date_label')}</div>
          <div>{t('tx_type_label')}</div>
          <div>{t('tx_amount_label')}</div>
          <div>{t('tx_balance_label')}</div>
          <div></div>
        </div>

        {loading && <Loader />}

        {!loading &&
          rows.map((row) => (
            <div
              key={row.id}
              className={styles.row}
              onClick={() => openSlip(row.ticket_id)}
              role={row.ticket_id ? 'button' : undefined}
              tabIndex={row.ticket_id ? 0 : -1}
            >
              <div>{row.ticket_id ?? row.id}</div>
              <div>{row.timestamp}</div>
              <div className={styles.transactionCell}>
                <span>{txTypeLabel(t, row.transaction_type)}</span>
                {row.casino_game_name ? (
                  <span className={styles.gameName}>
                    {row.casino_game_name}
                  </span>
                ) : null}
              </div>
              <div>{row.amount.toFixed(2)}</div>
              <div>{row.total_balance.toFixed(2)}</div>
              <div
                className={styles.chevron}
                title={row.ticket_id ? 'Show details' : undefined}
              >
                {row.ticket_id ? '›' : ''}
              </div>
            </div>
          ))}
      </div>

      <div className={styles.pagerRow}>
        <UgPager
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      <Modal
        isOpen={selectedSlip != null || loadingSlip}
        ariaHideApp={false}
        onRequestClose={() => setSelectedSlip(null)}
        className={styles.modal}
      >
        <div className={styles.modalHeader}>
          <span>
            {t('bet_slip_number_label')} {selectedSlip?.id ?? ''}
          </span>
          <button
            type="button"
            className={styles.modalClose}
            aria-label={t('close_label')}
            onClick={() => setSelectedSlip(null)}
          >
            ✕
          </button>
        </div>
        <div className={styles.modalBody}>
          {loadingSlip && <Loader />}
          {!loadingSlip && selectedSlip && (
            <SlipEvents slip={selectedSlip} defaultExpanded />
          )}
        </div>
        <div className={styles.modalFooter}>
          <button onClick={() => setSelectedSlip(null)}>
            {t('close_label')}
          </button>
        </div>
      </Modal>
    </div>
  );

  if (isMobile) {
    return (
      <MobileLayout pageTitle="account_statement_label">
        {selectedSlip ? (
          <SlipDetails
            slip={selectedSlip}
            setShowSlipDetails={setSelectedSlip}
          />
        ) : (
          <AccountStatementMobile
            rows={rows}
            loading={loading}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            selectedPeriod={selectedPeriod}
            onSelectedPeriodChange={setSelectedPeriod}
            startDate={startDate}
            onStartDateChange={setStartDate}
            endDate={endDate}
            onEndDateChange={setEndDate}
            transactionType={transactionType}
            onTransactionTypeChange={setTransactionType}
            onOpenSlip={openSlip}
          />
        )}
      </MobileLayout>
    );
  }
  return <Layout whiteBg>{content}</Layout>;
};
